import React, { useState, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { brawlerImage, initial } from '../data/meta';

/* Brawler avatar — falls back to the first letter on a tile if the
   icon is missing (the roster has ~90 icons but not every name). */
export function Avatar({ name, size, circle = false, className = '' }) {
  const [broken, setBroken] = useState(false);
  const style = size ? { width: size, height: size } : undefined;
  const fbStyle = size ? { fontSize: Math.round(size * 0.4) } : undefined;
  return (
    <span className={`av ${circle ? 'circle' : ''} ${className}`} style={style}>
      {broken ? (
        <span className="av-fb" style={fbStyle}>{initial(name)}</span>
      ) : (
        <img src={brawlerImage(name)} alt={name} loading="lazy" onError={() => setBroken(true)} />
      )}
    </span>
  );
}

export function TierBadge({ tier }) {
  return <span className={`tier ${tier}`}>{tier}</span>;
}

/* Map art keyed by the Brawl Stars event id (= image filename in
   public/media/maps). Tries .webp then .png, then falls back to a
   mode-tinted placeholder — many tracked maps have no committed image.
   `variant` is the container class ('thumb' for cards, 'map-shot' for hero). */
const MAP_EXTS = ['webp', 'png'];
export function MapThumb({ id, mode, variant = 'thumb' }) {
  const [ext, setExt] = useState(0);
  if (id == null || ext >= MAP_EXTS.length) {
    return <div className={`${variant} ph`}><span className="ph-mode">{mode}</span></div>;
  }
  return (
    <div className={variant}>
      <img
        src={`${process.env.PUBLIC_URL}/media/maps/${id}.${MAP_EXTS[ext]}`}
        alt={mode}
        loading="lazy"
        onError={() => setExt((e) => e + 1)}
      />
    </div>
  );
}

export function RateBar({ value, max = 60, lime = false }) {
  if (value == null) return <span style={{ color: 'var(--muted)' }}>—</span>;
  const v = parseFloat(value);
  const pctW = Math.max(4, Math.min(100, (v / max) * 100));
  return (
    <div className="rate-cell">
      <div className={`bar ${lime ? 'lime' : ''}`}><span style={{ width: pctW + '%' }} /></div>
      <span className="num">{v.toFixed(1)}%</span>
    </div>
  );
}

export function Loading() {
  return (
    <div className="state-box">
      <div className="spinner" />
      <p>Loading…</p>
    </div>
  );
}

/* ---- skeleton loaders — shaped placeholders beat a centered spinner ---- */

export function SkeletonRows({ rows = 8 }) {
  return (
    <div className="tablecard sk-pad">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="sk-row">
          <span className="sk sk-av" />
          <span className="sk sk-line" style={{ width: `${34 + ((i * 17) % 30)}%` }} />
          <span className="sk sk-line sk-right" />
        </div>
      ))}
    </div>
  );
}

export function SkeletonCards({ count = 6 }) {
  return (
    <div className="map-grid">
      {Array.from({ length: count }).map((_, i) => (
        <div key={i} className="map-card">
          <div className="sk sk-thumb" />
          <div className="mc-body">
            <span className="sk sk-line" style={{ width: '38%' }} />
            <span className="sk sk-line" style={{ width: '62%', marginTop: 10 }} />
          </div>
        </div>
      ))}
    </div>
  );
}

export function SkeletonHero() {
  return (
    <>
      <div className="bdhero">
        <span className="sk sk-av" style={{ width: 110, height: 110, borderRadius: 16 }} />
        <div style={{ flex: 1, minWidth: 200 }}>
          <span className="sk sk-line" style={{ width: '40%', height: 26 }} />
          <span className="sk sk-line" style={{ width: '55%', marginTop: 14 }} />
        </div>
      </div>
      <SkeletonRows rows={5} />
    </>
  );
}

export function ErrorMessage({ message, onRetry }) {
  return (
    <div className="state-box">
      <p>Something went wrong</p>
      <p className="err-d">{message}</p>
      {onRetry && (
        <button className="retry-btn" onClick={onRetry}>Try again</button>
      )}
    </div>
  );
}

/* In-system placeholder for features that need data the backend
   does not track yet (head-to-head matchups, counters, synergies). */
export function StubNote({ children }) {
  return (
    <div className="stub-note">
      <span className="soon">Coming soon</span>
      <p>{children}</p>
    </div>
  );
}

/* Per-page document titles and meta tags */
const DEFAULT_TITLE = 'StatYoyo — Brawl Stars & Clash Royale Stats';
export const DEFAULT_DESCRIPTION =
  'Live Brawl Stars meta and Clash Royale upgrade advice from tracked battles: tier lists, win rates, map picks and counters. Unofficial fan site, not affiliated with Supercell.';

function setHeadTag(attr, key, content) {
  let el = document.head.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

/* Per-route <title>, description, canonical and Open Graph / Twitter tags.
   The first page load arrives with these already set by the backend
   (site/backend/app/prerender.py), which mirrors these strings for crawlers
   and link-preview bots that don't run JavaScript — keep the two in step.
   This hook keeps them right as the visitor navigates. */
export function usePageMeta({ title, description, image, noindex = false }) {
  const { pathname } = useLocation();
  useEffect(() => {
    const fullTitle = title ? `${title} · StatYoyo` : DEFAULT_TITLE;
    const desc = description || DEFAULT_DESCRIPTION;
    const origin = window.location.origin;
    const url = origin + pathname;
    const img = origin + (image || '/og-image.png');

    document.title = fullTitle;
    setHeadTag('name', 'description', desc);
    setHeadTag('name', 'robots', noindex ? 'noindex,follow' : 'index,follow');
    setHeadTag('property', 'og:title', fullTitle);
    setHeadTag('property', 'og:description', desc);
    setHeadTag('property', 'og:url', url);
    setHeadTag('property', 'og:image', img);
    setHeadTag('name', 'twitter:title', fullTitle);
    setHeadTag('name', 'twitter:description', desc);
    setHeadTag('name', 'twitter:image', img);

    let canonical = document.head.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement('link');
      canonical.setAttribute('rel', 'canonical');
      document.head.appendChild(canonical);
    }
    canonical.setAttribute('href', url);

    // The server's structured data describes the page it rendered, not this one.
    const ld = document.getElementById('ssr-ld');
    if (ld && ld.dataset.path !== pathname) ld.remove();
  }, [title, description, image, noindex, pathname]);
}

/* Catches a render crash in one page so the rest of the site (nav, footer)
   stays up instead of the whole screen going blank. It clears itself when
   resetKey changes, so navigating away recovers. */
export class PageErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { failed: false };
  }

  static getDerivedStateFromError() {
    return { failed: true };
  }

  componentDidUpdate(prev) {
    if (this.state.failed && prev.resetKey !== this.props.resetKey) {
      this.setState({ failed: false });
    }
  }

  componentDidCatch(err, info) {
    console.error('Page crashed', err, info?.componentStack);
  }

  render() {
    if (this.state.failed) {
      return (
        <div className="page"><div className="wrap">
          <ErrorMessage
            message="This page hit an error. Try again, or go back and pick another page."
            onRetry={() => this.setState({ failed: false })}
          />
        </div></div>
      );
    }
    return this.props.children;
  }
}
